import { api } from "./client";
import type { Member } from "./members";
import type { Order } from "./cart";
import type { BanquetEvent, BanquetReservation } from "./banquet";

export async function adminLogin(email: string, password: string) {
  const { data } = await api.post("/api/auth/admin-login", { email, password });
  return data as { jwt: string; email: string; name: string };
}

export async function listMembers(params: {
  status?: string;
  tier?: string;
  tag?: string;
  search?: string;
  referred_by_code?: string;  // 顧問碼篩選;特殊值 __none__ = 未填推薦碼
  limit?: number;
  offset?: number;
}) {
  const { data } = await api.get("/api/admin/members", { params });
  return data as { members: Member[]; count: number };
}

export async function adminCreateMember(payload: {
  name: string;
  phone: string;
  email?: string;
  birthday?: string;
  tier?: string;
  address?: { city?: string; district?: string; detail?: string };
  referred_by_code?: string;
}) {
  // 400(phone_taken / invalid)不要讓 axios throw — 讓呼叫端讀 status 顯示正確訊息(否則一律落到 catch 變「連線異常」)
  const { data } = await api.post("/api/admin/members", payload, {
    validateStatus: (s) => (s >= 200 && s < 300) || s === 400,
  });
  return data as { status: string; line_user_id?: string; hint?: string; existing_uid?: string; existing_name?: string };
}

export async function getMember360(uid: string) {
  const { data } = await api.get(`/api/admin/members/${uid}`);
  return data;
}

export async function adminUpdateMember(uid: string, fields: {
  name?: string;
  phone?: string;
  email?: string;
  birthday?: string;
  address?: { city?: string; district?: string; detail?: string };
  referred_by_code?: string;  // 帶此 key:有值=設定/修正,空字串=清除推薦來源歸因
  admin_note?: string;        // 管理者備註(admin-only,客戶前台看不到)
}) {
  const { data } = await api.patch(`/api/admin/members/${uid}`, fields);
  return data as { status: string; updated?: string[] };
}


// ── Onboarding 表單歷史(debug user typo / autofill case 用)─────────

export interface OnboardingSubmission {
  id: string;
  line_user_id: string;
  submitted_at: string;
  form: {
    name: string;
    phone: string;
    email: string;
    email_raw_len: number;
    birthday: string;
    address_city: string;
    address_district: string;
    address_detail: string;
    referral_code?: string;
    consent: boolean;
  };
  audit: {
    ip: string;
    user_agent: string;
  };
  result: "ok" | "already_active" | "phone_taken" | "invalid";
}

export async function adminListOnboardingSubmissions(uid: string) {
  const { data } = await api.get(`/api/admin/members/${uid}/submissions`);
  return data as { submissions: OnboardingSubmission[]; count: number };
}

/**
 * 後台改會籍等級。period_mode(只對付費等級有作用):
 *   auto(預設)= 依 app_config.membership_period 自動帶入期間(預設次月一日起 12 個月)
 *   keep        = 只改等級,期間不動
 *   custom      = 用 membership_start / membership_end(YYYY-MM-DD)
 * status:ok / period_only(同等級只重設期間)/ no_change。400 / 404 的錯誤也要讓呼叫端讀 status。
 */
export async function adminSetTier(
  uid: string,
  tier: string,
  reason?: string,
  opts?: {
    period_mode?: "auto" | "custom" | "keep";
    membership_start?: string;
    membership_end?: string;
  },
) {
  const { data } = await api.post(
    `/api/admin/members/${uid}/tier`,
    { tier, reason, ...(opts || {}) },
    { validateStatus: (s) => (s >= 200 && s < 300) || s === 400 || s === 404 },
  );
  return data as {
    status: string;
    from?: string;
    to?: string;
    membership_start?: string | null;
    membership_end?: string | null;
  };
}

export async function adminExtendMembership(uid: string, extraDays: number) {
  const { data } = await api.post(`/api/admin/members/${uid}/membership`, { extra_days: extraDays });
  return data as { status: string; membership_end?: string };
}

/** 直接指定會籍到期日(起算日選填)。日期 YYYY-MM-DD,台北日期。status:ok / invalid_date / end_before_start / member_not_found */
export async function adminSetMembershipDates(
  uid: string,
  payload: { membership_end: string; membership_start?: string },
) {
  const { data } = await api.post(`/api/admin/members/${uid}/membership-dates`, payload, {
    validateStatus: (s) => (s >= 200 && s < 300) || s === 400 || s === 404,
  });
  return data as {
    status: string;
    membership_start?: string | null;
    membership_end?: string | null;
  };
}

/** 重打 LINE profile API 更新暱稱 + 頭像。取不到(對方封鎖官方帳號等)回 status=profile_unavailable(HTTP 200) */
export async function adminRefreshLineProfile(uid: string) {
  const { data } = await api.post(`/api/admin/members/${uid}/refresh-line-profile`, {}, {
    validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
  });
  return data as { status: string; display_name?: string; picture_url?: string };
}

// ── 消費金額手動調整(可加可減,留 audit)──────────
export interface SpendAdjustment {
  id: string;
  amount: number;
  before: number;
  after: number;
  note: string;
  operator: string;
  created_at: string;
}

export async function adminAdjustSpent(uid: string, amount: number, note: string = "") {
  const { data } = await api.post(`/api/admin/members/${uid}/adjust-spent`, { amount, note }, {
    validateStatus: (s) => (s >= 200 && s < 300) || s === 400 || s === 404,
  });
  return data as { status: string; before?: number; after?: number; delta?: number };
}

export async function adminListSpendAdjustments(uid: string) {
  const { data } = await api.get(`/api/admin/members/${uid}/spend-adjustments`);
  return data as { adjustments: SpendAdjustment[]; count: number };
}

// ── 恆時薈入會配額(即飲方案 / 鮮燉方案,二選一)──────────
export interface TimelessPlan {
  key: string;     // drink / fresh
  name: string;    // 即飲方案 / 鮮燉方案
  grants: { category: string; qty: number }[];
}

export async function adminListTimelessPlans() {
  const { data } = await api.get("/api/admin/timeless-plans");
  return data as { plans: TimelessPlan[] };
}

export async function adminGrantTimeless(uid: string, plan: string) {
  const { data } = await api.post(`/api/admin/members/${uid}/timeless-grant`, { plan }, {
    validateStatus: (s) => (s >= 200 && s < 300) || s === 400,
  });
  return data as {
    status: string;
    plan?: string;
    plan_name?: string;
    granted?: { category: string; qty: number; status: string }[];
  };
}

export async function adminSetMemberStatus(uid: string, status: "active" | "suspended") {
  const { data } = await api.post(`/api/admin/members/${uid}/status`, { status });
  return data as { status: string; new_status?: string };
}

export async function adminAddTag(uid: string, tag: string) {
  const { data } = await api.post(`/api/admin/members/${uid}/tags`, { tag });
  return data as { status: string; tags?: string[] };
}

export async function adminRemoveTag(uid: string, tag: string) {
  const { data } = await api.delete(`/api/admin/members/${uid}/tags`, { data: { tag } });
  return data as { status: string; tags?: string[] };
}

// ── 整理 / 刪除 / 合併 重複帳號 ─────────────

export async function adminDeleteMember(uid: string, force = false) {
  const { data } = await api.delete(`/api/admin/members/${uid}`, {
    params: force ? { force: "true" } : {},
    // 後端用 404(member_not_found)/ 409(has_history)帶語意 — 視為正常回應,
    // 否則 axios 直接 throw,呼叫端拿不到 status 走不進強制刪流程
    validateStatus: (s) => (s >= 200 && s < 300) || s === 404 || s === 409,
  });
  return data as {
    status: string;
    deleted?: Record<string, number>;
    kept_orders?: number;
    orders?: number;          // has_history 時回(目前訂單數)
    wallet_bottles?: number;  // has_history 時回(存摺餘額)
    hint?: string;
  };
}

export async function adminMergeMembers(primaryUid: string, secondaryUid: string) {
  const { data } = await api.post(`/api/admin/members/${primaryUid}/merge`, {
    secondary_uid: secondaryUid,
  });
  return data as {
    status: string;
    primary_uid?: string;
    secondary_uid?: string;
    moved?: Record<string, number>;
  };
}

export interface DuplicateMemberSummary {
  line_user_id: string;
  name: string;
  phone: string;
  email: string;
  tier: string;
  status: string;
  source: string;
  total_orders: number;
  created_at: string | null;
}

export interface DuplicateGroup {
  type: "phone" | "name";
  key: string;
  members: DuplicateMemberSummary[];
}

export async function adminListDuplicateMembers() {
  const { data } = await api.get("/api/admin/members/duplicates");
  return data as { groups: DuplicateGroup[]; count: number };
}

// 會員實際用過的推薦來源(給會員列表「推薦來源」篩選下拉 + 分潤概覽)
export interface ReferralSource {
  code: string;
  label: string;
  resolved: boolean;
  count: number;
}

export async function adminListReferralSources() {
  const { data } = await api.get("/api/admin/members/referral-sources");
  return data as { sources: ReferralSource[]; count: number };
}

// 會員實際出現過的標籤(含 #16 偏好問卷寫入的「對象:/品項:/頻率:」與後台手動貼的),給會員列表「標籤」篩選下拉
export async function adminListMemberTags(): Promise<{ tags: { tag: string; count: number }[] }> {
  const { data } = await api.get("/api/admin/members/tags");
  return data;
}

export async function exportMembersCsv(): Promise<Blob> {
  const r = await api.get("/api/admin/members/export.csv", {
    responseType: "blob",
  });
  return r.data;
}

export async function getConfig() {
  const { data } = await api.get("/api/admin/config");
  return data.config;
}

export async function updateConfig(patch: any) {
  const { data } = await api.put("/api/admin/config", patch);
  return data;
}

// ── 訂單管理(#10 Phase 1.5)────────────────

export async function adminListOrders(params: {
  status?: string;
  member_uid?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
}) {
  const { data } = await api.get("/api/admin/orders", { params });
  return data as { orders: Order[]; count: number };
}

export async function adminGetOrder(order_id: string) {
  const { data } = await api.get(`/api/admin/orders/${order_id}`);
  return data.order as Order;
}

export async function adminCreateOrder(payload: {
  member_uid: string;
  items: { product_id: string; qty: number }[];
  address?: { city?: string; district?: string; detail?: string };
  note?: string;
  initial_status?: "paid" | "shipped" | "pending_payment_mock";
  ship_date?: string;          // YYYY-MM-DD,initial_status=shipped 時生效
  discount_amount?: number;    // 折讓 / 折扣金額;實收 = 原價 − 折讓,業績用實收計(不影響入帳瓶數)
  discount_note?: string;      // 折讓原因(對帳 / audit,選填)
  idempotency_key?: string;    // 冪等 key(uuid)— 同 key 重送回原本那筆,不會重複建單/入帳
}) {
  const { data } = await api.post("/api/admin/orders", payload);
  return data as { status: string; order_id?: string };
}

// 編輯訂單(收件資訊 / 備註 / 明細)— 結案訂單拒改
export async function adminUpdateOrder(
  order_id: string,
  patch: {
    name?: string;
    phone?: string;
    address?: { city?: string; district?: string; detail?: string };
    note?: string;
    admin_note?: string;
    items?: { product_id: string; qty: number; mode?: string }[];
    discount_amount?: number;   // 折讓 / 折扣金額;實收重算,已付款單會同步修正累計消費
    discount_note?: string;     // 折讓原因(對帳 / audit,選填)
  },
) {
  const { data } = await api.patch(`/api/admin/orders/${order_id}`, patch);
  return data as {
    status: string;
    order_id?: string;
    order?: Order;
    current?: string;       // not_editable 時回目前狀態
    product_id?: string;    // 商品錯誤回 pid
    shipped_bottles?: number;  // qty_below_shipped 時回已出貨瓶數
    min_qty?: number;          // qty_below_shipped 時回至少要訂幾件
    requested?: number;
  };
}


export async function adminUpdateOrderStatus(
  order_id: string,
  status: string,
  note: string = "",
  tracking?: { carrier: string; number: string },
  ship_date?: string,   // YYYY-MM-DD,status=shipped 時生效
) {
  const body: any = { status, note };
  if (tracking && (tracking.carrier || tracking.number)) {
    body.tracking = tracking;
  }
  if (ship_date) {
    body.ship_date = ship_date;
  }
  const { data } = await api.patch(`/api/admin/orders/${order_id}/status`, body);
  return data;
}

// 分批出貨 — 新增一筆出貨批次
export async function adminAddShipment(
  order_id: string,
  payload: {
    lines: { product_id: string; mode: string; qty: number }[];  // qty = 瓶數
    carrier: string;
    number?: string;
    ship_date?: string;   // YYYY-MM-DD,空 → 後端用今天
    note?: string;
  },
) {
  const { data } = await api.post(`/api/admin/orders/${order_id}/shipments`, payload);
  return data as {
    status: string;
    shipment_id?: string;
    new_status?: string;
    remaining_total?: number;   // 瓶
    remaining?: number;         // over_ship 時回該品項剩餘瓶數
    // 出貨 → 存摺提領同步結果:ok / no_credit_skip(未入帳單不扣)/ partial_failed(餘額不足,人工對帳)
    wallet_debit?: { status: string; failed_lines?: Record<string, { qty: number; reason: string }> };
  };
}

// ── 存摺直接出貨(寄存提領,不經訂單)──

export interface WalletShipmentLine {
  category: string;   // drink / fresh / giftbox / box / bag / legacy(既有餘額)
  name: string;
  unit: string;
  qty: number;
}

export interface WalletShipment {
  shipment_id: string;
  member_uid: string;
  member_name: string;
  phone: string;
  lines: WalletShipmentLine[];
  carrier: string;
  number: string;
  ship_date: string;
  note: string;
  operator: string;
  created_at?: string;
}

// 全有全無:任一品類餘額不足整筆拒(insufficient_balance + category);冪等 key 防重扣
export async function adminCreateWalletShipment(payload: {
  member_uid: string;
  lines: { category: string; qty: number }[];
  carrier?: string;
  number?: string;
  ship_date?: string;
  note?: string;
  idempotency_key?: string;
}) {
  const { data } = await api.post("/api/admin/wallet-shipments", payload);
  return data as {
    status: string;
    shipment?: WalletShipment;
    idempotent?: boolean;
    category?: string;   // insufficient_balance / invalid_* 時回哪個品類
    balance?: number;
  };
}

export async function adminListWalletShipments(member_uid: string, limit = 10) {
  const { data } = await api.get("/api/admin/wallet-shipments", { params: { member_uid, limit } });
  return data as { shipments: WalletShipment[]; count: number };
}

// ── 出貨管理(全部歷史出貨紀錄 + Excel 匯出)──

export interface ShipmentRecord {
  key: string;                 // "order:{oid}:{sid}" / "wallet:{sid}"(勾選 / 匯出用)
  source: "order" | "wallet";
  order_id: string;            // source=order 才有
  ship_date: string;
  member_uid: string;
  member_name: string;
  phone: string;
  lines: { name: string; qty: number; unit: string }[];
  carrier: string;
  number: string;
  note: string;
  operator: string;
}

export async function adminListAllShipments(limit = 500) {
  const { data } = await api.get("/api/admin/shipments", { params: { limit } });
  return data as { shipments: ShipmentRecord[]; count: number };
}

// 匯出勾選訂單 .xlsx(一列一張訂單,原價/折讓/實收可直接加總);回 Blob 由呼叫端觸發下載
export async function adminExportOrders(order_ids: string[]) {
  const { data } = await api.post(
    "/api/admin/orders/export",
    { order_ids },
    { responseType: "blob", timeout: 60000 },
  );
  return data as Blob;
}

// 匯出 .xlsx(keys 空陣列 = 全部);回 Blob 由呼叫端觸發下載
export async function adminExportShipments(keys: string[]) {
  const { data } = await api.post(
    "/api/admin/shipments/export",
    { keys },
    { responseType: "blob", timeout: 60000 },
  );
  return data as Blob;
}

// ── 出貨預估(#15 回購預測 B2B 版:存摺未出貨餘額 + 本週 / 本月預計出貨)──

/** 品類彙總。category:drink/fresh/giftbox/dry/box/bag + legacy(舊制既有餘額,單位瓶) */
export interface ForecastCategory {
  category: string;
  name: string;
  unit: string;
  total_balance: number;       // 全持有人未出貨餘額(存摺尚未提領)
  holders: number;             // 持有該品類餘額的人數
  idle_holders: number;        // 其中近 N 週沒提領該品類的人數
  shipped_this_week: number;   // 台北週一 00:00 起
  shipped_this_month: number;
  shipped_last_month: number;
  projected_week: number;      // 依近 N 週出貨速率推估,供叫貨參考
  projected_month: number;
}

export interface ForecastMemberCat {
  balance: number;
  shipped_this_week: number;
  shipped_this_month: number;
  weekly_pace: number;
  projected_week: number;
  projected_month: number;
  last_shipped_at: string | null;
}

export interface ForecastMemberRow {
  line_user_id: string;
  name: string;                // 真名優先,否則 LINE 暱稱
  phone: string;
  tier: string;
  cats: Record<string, ForecastMemberCat>;   // 只含還有餘額的品類
  idle: boolean;               // 近 N 週任何品類都沒提領
  last_shipped_at: string | null;
}

export interface ForecastData {
  as_of: string;
  window_weeks: number;
  week_start: string;
  month_start: string;
  categories: ForecastCategory[];
  members: ForecastMemberRow[];
  holders_total: number;
}

export async function adminGetForecast(weeks = 8) {
  const { data } = await api.get("/api/admin/forecast", { params: { weeks } });
  return data as ForecastData;
}

// 匯出 .xlsx(品類彙總 + 會員明細兩張工作表);回 Blob 由呼叫端觸發下載
export async function adminExportForecast(weeks: number): Promise<Blob> {
  const { data } = await api.post(
    "/api/admin/forecast/export",
    { weeks },
    { responseType: "blob", timeout: 60000 },
  );
  return data as Blob;
}

// 硬刪除訂單(髒資料 / 測試單清理)— 已 credit 的瓶數不會回退
export async function adminDeleteOrder(order_id: string) {
  const { data } = await api.delete(`/api/admin/orders/${order_id}`);
  return data as { status: string; order_id?: string };
}


// 標記訂單已付款(觸發存摺入帳 + 推薦碼獎勵)
export async function adminMarkOrderPaid(order_id: string) {
  const { data } = await api.post(`/api/admin/orders/${order_id}/mark-paid`, {});
  return data as { status: string; order_id?: string; current?: string };
}

/** 街口全額退款 — 錢退回購買人街口帳戶,成功後端自動取消訂單(沖瓶數 + 通知客戶) */
export async function adminJkopayRefund(order_id: string) {
  const { data } = await api.post(`/api/admin/orders/${order_id}/jkopay-refund`, {});
  return data as { status: string; refund?: { amount: number; refund_tradeNo?: string }; cancel?: { status: string } };
}

// ── 推薦碼 / 優惠碼 / 顧問碼 管理 ──────────

export interface Code {
  code: string;
  type: "member" | "campaign" | "consultant";
  active: boolean;
  label: string;
  owner_uid: string;
  used_count: number;
  discount_type?: "amount" | "percent";
  discount_value?: number;
  valid_from?: string;
  valid_to?: string;
  usage_limit?: number;
  referee_bottles?: number;
  referrer_bottles?: number;
  created_by?: string;
  created_at?: string;
}

export async function adminListCodes(type?: string) {
  const { data } = await api.get("/api/admin/codes", { params: type ? { type } : {} });
  return data as { codes: Code[]; count: number };
}

export async function adminCreateCode(payload: {
  type: "campaign" | "consultant";
  code?: string;
  label: string;
  discount_type?: "amount" | "percent";
  discount_value?: number;
  valid_from?: string;
  valid_to?: string;
  usage_limit?: number;
  referee_bottles?: number;
  referrer_bottles?: number;
  owner_uid?: string;
  active?: boolean;
}) {
  const { data } = await api.post("/api/admin/codes", payload);
  return data as { status: string; code?: string; hint?: string };
}

export async function adminToggleCode(code: string, active: boolean) {
  const { data } = await api.post(`/api/admin/codes/${code}/active`, { active });
  return data as { status: string };
}

// 訂單狀態中文 label(對齊後端 order_service.ORDER_STATUS_LABELS)
export const ORDER_STATUS_LABELS: Record<string, string> = {
  pending_payment: "待付款",           // 街口單:沒付款絕不能出貨(跟 mock 的「待出貨」分開)
  pending_payment_mock: "待出貨",
  paid: "待出貨",
  processing: "處理中",
  partially_shipped: "部分出貨",
  shipped: "已出貨",
  completed: "已完成",
  cancelled: "已取消",
};

// 客服可手動切換的狀態(對齊後端 ADMIN_MUTABLE_STATUSES)
export const ADMIN_MUTABLE_STATUSES = ["shipped", "completed", "cancelled"] as const;

// 物流商代號 + 中文 label(對齊後端 flex/order_status_notification.CARRIER_LABELS)
export const CARRIER_OPTIONS: { value: string; label: string }[] = [
  { value: "blackcat_room", label: "黑貓常溫" },
  { value: "blackcat", label: "黑貓宅配" },
  { value: "post", label: "郵局" },
  { value: "other", label: "其他" },
];

// 顯示用 label map — 含舊代號 fallback,讓歷史訂單仍能正確顯示物流名稱
export const CARRIER_LABELS: Record<string, string> = {
  ...Object.fromEntries(CARRIER_OPTIONS.map((o) => [o.value, o.label])),
  "711": "7-11 取貨",
  familymart: "全家取貨",
  sf: "順豐速運",
};


// ── 帳號 ────────────────

export async function adminChangePassword(old_password: string, new_password: string) {
  const { data } = await api.post("/api/admin/change-password", { old_password, new_password });
  return data as { status: string };
}


// ── Dashboard 首頁 ────────────────

export type DashboardKpis = {
  today_orders_count: number;
  today_orders_amount: number;
  month_revenue: number;
  pending_orders_count: number;
  active_members_7d: number;
};

export type DashboardOrderSummary = {
  order_id: string;
  member_uid: string;
  name: string;
  phone: string;
  total: number;
  status: string;
  created_at: string | null;
};

export type DashboardBirthdayItem = {
  line_user_id: string;
  name: string;
  tier: string;
  birthday: string;
  days_until: number;
};

export type DashboardExpiringItem = {
  line_user_id: string;
  name: string;
  tier: string;
  membership_end: string;
  days_left: number;
};

export type DashboardSleepingItem = {
  line_user_id: string;
  name: string;
  tier: string;
  last_interaction_at: string | null;
  days_silent: number | null;
};

export type DashboardInteraction = {
  member_uid: string;
  type: string;
  content: string;
  timestamp: string;
};

export type DashboardTrendPoint = {
  date: string;
  count: number;
  revenue: number;
};

export type DashboardProductRank = {
  product_id: string;
  name: string;
  qty: number;
  revenue: number;
};

export type DashboardData = {
  kpis: DashboardKpis;
  todos: {
    pending_orders: DashboardOrderSummary[];
    birthday_upcoming: DashboardBirthdayItem[];
    membership_expiring: DashboardExpiringItem[];
    sleeping_members: DashboardSleepingItem[];   // 只有前 5
    sleeping_total: number;                       // 全名單人數(2026-10-06)
    sleeping_days: number;                        // 目前門檻(app_config.churn.sleeping_days)
    sleeping_exclude_general: boolean;
  };
  recent: {
    recent_orders: DashboardOrderSummary[];
    recent_interactions: DashboardInteraction[];
  };
  trends: {
    series: DashboardTrendPoint[];
    total_count: number;
    total_revenue: number;
    avg_order_value: number;
  };
  product_ranking: DashboardProductRank[];
  tier_distribution: {
    counts: Record<string, number>;
    total: number;
  };
  range_days: number;
};

export async function getAdminDashboard(rangeDays: number = 30): Promise<DashboardData> {
  const { data } = await api.get("/api/admin/dashboard", { params: { range: rangeDays } });
  return data as DashboardData;
}


// ── #17 久未互動會員全名單 + 匯出(2026-10-06;儀表板卡片只放前 5)──────────

export interface SleepingMemberRow {
  line_user_id: string;
  name: string;
  display_name: string;
  phone: string;
  tier: string;
  last_interaction_at: string | null;
  days_silent: number | null;   // null = 從未互動
  membership_end: string | null;
  total_spent: number;
}

export interface SleepingList {
  days: number;
  exclude_general: boolean;
  total: number;
  members: SleepingMemberRow[];
}

/** 不帶 days / exclude_general = 用後台設定值;帶了可臨時看別的門檻 */
export async function adminListSleepingMembers(params: { days?: number; exclude_general?: boolean } = {}) {
  const { data } = await api.get("/api/admin/members/sleeping", { params });
  return data as SleepingList;
}

export async function adminExportSleepingMembers(params: { days?: number; exclude_general?: boolean } = {}): Promise<Blob> {
  const { data } = await api.get("/api/admin/members/sleeping/export.xlsx", { params, responseType: "blob" });
  return data as Blob;
}


// ── #2 到期提醒排程預覽(今天跑會推給誰;不推、不寫台帳)──────────

export interface TierExpiryPreviewRow {
  line_user_id: string;
  name: string;
  tier: string;
  membership_end: string | null;
  already_sent: boolean;
}

export interface TierExpiryPreview {
  reminder_days: number[];
  milestones: { days: number; members: TierExpiryPreviewRow[] }[];
  just_expired: TierExpiryPreviewRow[];
  errors: string[];
}

export async function adminTierExpiryPreview() {
  const { data } = await api.get("/api/admin/tier-expiry/preview");
  return data as TierExpiryPreview;
}


// ── 販售品項 admin CRUD ─────────────

export interface AdminProduct {
  id: string;
  category: string;
  name: string;
  spec: string;
  price: number;
  bottle_count: number;
  shipping_fee: number;
  tagline: string;
  image_filename: string;
  active: boolean;
  storefront_hidden?: boolean;  // 前台隱藏(LINE 卡+選購頁不顯示,人工建單仍可選)
  sort_order: number;
  updated_at?: string;
  updated_by?: string;
}

export interface ProductCategoryMeta {
  key: string;
  title: string;
  subtitle: string;
  image_filename: string;
  sort_order?: number;
  active?: boolean;
  usage_guide?: { intro?: string; sections?: { title: string; body: string }[] };
}

export async function adminListProducts(): Promise<AdminProduct[]> {
  const { data } = await api.get("/api/admin/products");
  return data.products;
}

export async function adminListProductCategories(): Promise<ProductCategoryMeta[]> {
  const { data } = await api.get("/api/admin/products/categories");
  return data.categories;
}

export async function adminGetProduct(productId: string): Promise<AdminProduct> {
  const { data } = await api.get(`/api/admin/products/${productId}`);
  return data.product;
}

export async function adminUpsertProduct(payload: Partial<AdminProduct> & { product_id: string }) {
  const { data } = await api.post("/api/admin/products", payload);
  return data as { status: string; created?: boolean; product_id?: string; hint?: string };
}

// 上傳商品圖 → GCS bucket,回新檔名(帶時間戳、不覆蓋舊檔,防快取殘影)
// axios 對 FormData 會自帶 multipart boundary,不手動設 Content-Type
export async function adminUploadProductImage(file: File) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/api/admin/products/upload-image", fd, {
    timeout: 60000,   // 圖片比一般 API 慢,放寬到 60s
  });
  return data as { status: string; filename?: string; url?: string };
}

export async function adminToggleProductActive(productId: string, active: boolean) {
  const { data } = await api.post(`/api/admin/products/${productId}/active`, { active });
  return data as { status: string };
}

export async function adminDeleteProduct(productId: string) {
  const { data } = await api.delete(`/api/admin/products/${productId}`);
  return data as { status: string };
}

export async function adminSeedProducts(overwrite: boolean = false) {
  const { data } = await api.post("/api/admin/products/seed", { overwrite });
  return data as { status: string; created: number; skipped: number };
}


// ── 商品分類管理(#5 自訂分類)─────────────

export async function adminListAllCategories(): Promise<ProductCategoryMeta[]> {
  // 含停用分類(管理頁用);商品下拉用 adminListProductCategories(只 active)
  const { data } = await api.get("/api/admin/products/categories", { params: { all: 1 } });
  return data.categories;
}

export async function adminUpsertCategory(payload: {
  key?: string;
  title: string;
  subtitle?: string;
  image_filename?: string;
  sort_order?: number;
  active?: boolean;
  usage_guide?: { intro: string; sections: { title: string; body: string }[] };
}) {
  const { data } = await api.post("/api/admin/products/categories", payload);
  return data as { status: string; key?: string; created?: boolean; hint?: string };
}

export async function adminDeleteCategory(key: string) {
  const { data } = await api.delete(`/api/admin/products/categories/${key}`);
  return data as { status: string; hint?: string };
}

export async function adminToggleCategoryActive(key: string, active: boolean) {
  const { data } = await api.post(`/api/admin/products/categories/${key}/active`, { active });
  return data as { status: string };
}

export async function adminSeedCategories(overwrite: boolean = false) {
  const { data } = await api.post("/api/admin/products/categories/seed", { overwrite });
  return data as { status: string; created: number; skipped: number };
}


// ── 溯源數位憑證管理(#11)─────────────

export interface TraceabilityDoc {
  doc_id: string;
  batch_range: { from: number; to: number };
  house_id: string;
  headline_zh: string;
  headline_en: string;
  location: {
    country_zh: string;
    state_zh: string;
    district_zh: string;
    district: string;
    gps: { lat: number; lng: number };
  };
  building: {
    description_zh: string;
    floors: number;
    area_sqft: number;
    founded_year: number;
    harvesting_years: number;
  };
  certification: {
    authority_zh: string;
    authority_full: string;
    reviewer_zh: string;
    date: string;
  };
  images: { hero: string; gallery: string[] };
  captions_zh: string[];
  narrative_zh: string[];
  source_url?: string;
}

export async function adminListTraceability(): Promise<TraceabilityDoc[]> {
  const { data } = await api.get("/api/admin/traceability");
  return data.traceability;
}

export async function adminUpsertTraceability(payload: Record<string, unknown> & { doc_id?: string }) {
  const { data } = await api.post("/api/admin/traceability", payload);
  return data as { status: string; doc_id?: string; created?: boolean; hint?: string };
}

export async function adminDeleteTraceability(docId: string) {
  const { data } = await api.delete(`/api/admin/traceability/${docId}`);
  return data as { status: string };
}


// ── 品牌活動管理(LINE carousel 卡片 + 完整頁,後台自建)─────────────

export interface BrandEventDoc {
  slug: string;
  title_zh: string;
  title_en: string;
  date: string;
  venue: string;
  card_image: string;
  card_lines: string[];
  has_detail: boolean;
  active: boolean;
  sort_order: number;
  eyebrow: string;
  hero_image: string;
  hero_aspect: "4/3" | "3/4";
  lead: string;
  intro: string[];
  sections: { icon?: string; title: string; body: string }[];
  closing: string[];
  tags: string[];
  gallery: string[];
  related_product: { name: string; spec: string; note?: string; product_id?: string };
}

export async function adminListBrandEvents(): Promise<BrandEventDoc[]> {
  const { data } = await api.get("/api/admin/brand-events");
  return data.events;
}

export async function adminUpsertBrandEvent(payload: Record<string, unknown> & { slug?: string }) {
  const { data } = await api.post("/api/admin/brand-events", payload);
  return data as { status: string; slug?: string; created?: boolean; hint?: string };
}

export async function adminDeleteBrandEvent(slug: string) {
  const { data } = await api.delete(`/api/admin/brand-events/${slug}`);
  return data as { status: string };
}

export async function adminToggleBrandEvent(slug: string, active: boolean) {
  const { data } = await api.post(`/api/admin/brand-events/${slug}/active`, { active });
  return data as { status: string; active?: boolean };
}

export async function adminUploadBrandEventImage(file: File) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/api/admin/brand-events/upload`, form);
  return data as { status: string; url?: string };
}

export async function adminSeedBrandEvents(overwrite = false) {
  const { data } = await api.post("/api/admin/brand-events/seed", { overwrite });
  return data as { status: string; created: number; skipped: number };
}


// ── 互動分析(#16)─────────────

export interface InteractionRow {
  member_uid: string;
  type: string;
  content: string;
  timestamp: string | null;
}

export interface InteractionStats {
  total_sampled: number;
  ranking: { type: string; content: string; count: number }[];
  by_type: { type: string; count: number }[];
}

export async function adminListInteractions(params: { type?: string; limit?: number }): Promise<InteractionRow[]> {
  const { data } = await api.get("/api/admin/interactions", { params });
  return data.interactions;
}

export async function adminInteractionStats(): Promise<InteractionStats> {
  const { data } = await api.get("/api/admin/interactions/stats");
  return data;
}

// ── 私宴預約管理(場次自助 CRUD + 預約清單)─────────────

export interface BanquetEventForm {
  title?: string;
  title_en?: string;
  tagline?: string;
  /** YYYY-MM-DD(date picker;顯示用 date_label 後端自動衍生) */
  event_date?: string;
  time_label?: string;
  venue?: string;
  venue_address?: string;
  fee_label?: string;
  capacity?: number;
}

export async function adminListBanquetEvents() {
  const { data } = await api.get("/api/admin/banquet/events");
  return data as { events: BanquetEvent[] };
}

export async function adminCreateBanquetEvent(payload: BanquetEventForm) {
  const { data } = await api.post("/api/admin/banquet/events", payload);
  return data as { status: string; event_id?: string };
}

export async function adminUpdateBanquetEvent(eid: string, payload: BanquetEventForm & { status?: string }) {
  const { data } = await api.patch(`/api/admin/banquet/events/${eid}`, payload);
  return data as { status: string; conflict_title?: string };
}

export async function adminUploadBanquetHero(eid: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/api/admin/banquet/events/${eid}/hero`, form);
  return data as { status: string; hero_url?: string; hero_w?: number; hero_h?: number };
}

export async function adminUploadTraceabilityImage(file: File) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/api/admin/traceability/upload`, form);
  return data as { status: string; url?: string };
}

export async function adminListBanquetReservations(eventId?: string) {
  const { data } = await api.get("/api/admin/banquet/reservations", {
    params: eventId ? { event_id: eventId } : {},
  });
  return data as {
    event: BanquetEvent | null;
    events: BanquetEvent[];
    reservations: BanquetReservation[];
    seats: number;
    waitlist_seats: number;
    capacity: number;
  };
}

// 候補轉正 / 已取消恢復時席位不夠 → 400 { status: "no_seats", remaining }(axios 會 throw,從 err.response.data 讀)
export async function adminUpdateBanquetReservation(rid: string, status: string) {
  const { data } = await api.patch(`/api/admin/banquet/reservations/${rid}`, { status });
  return data as { status: string; new_status?: string };
}

/** 後台代報:管理者替會員或非會員來賓登記私宴席次 */
export interface AdminBanquetReservePayload {
  event_id: string;
  /** 會員模式帶會員的 line_user_id;非會員來賓不帶或帶 "" */
  member_uid?: string;
  name: string;
  phone: string;
  /** 1-4 */
  party_size: number;
  note?: string;
  /** 預設 pending;席位不夠時後端自動改候補 */
  status?: "pending" | "confirmed";
}

// 席位不夠 → 200 但 waitlist=true、final_status="waitlist"(後端自動列候補)
// 失敗 400(axios 會 throw,從 err.response.data 讀):
//   status = already_reserved(附 reservation)/ phone_is_member(附 member_uid、member_name)/
//            member_not_found / missing_fields / invalid_phone / invalid_party_size / invalid_status / event_not_found
export async function adminCreateBanquetReservation(payload: AdminBanquetReservePayload) {
  const { data } = await api.post("/api/admin/banquet/reservations", payload);
  return data as {
    status: string;
    reservation_id?: string;
    waitlist?: boolean;
    final_status?: string;
    notified?: boolean;
  };
}


// ════════════════════════════════════════════════════
// 叫貨與出貨(/api/admin/supply/*)
//
// 錯誤處理統一:會被後端用 400 / 404 + {status: "<code>"} 擋下的寫入動作,都帶 validateStatus,
// 回傳 {status, ...} 讓呼叫端讀 status 顯示中文訊息(對照表在 utils/supply.ts 的 supplyErrorText);
// 只有網路 / 5xx / 401 才會 throw。GET 總覽類(overview / settings / plans / raw / batch)失敗就 throw。
// ════════════════════════════════════════════════════

export interface SupplyQty {
  drink: number;   // 即飲
  fresh: number;   // 鮮燉
}

export type SupplyBatchStatus = "planned" | "draft" | "ordered" | "arrived" | "shipping" | "shipped";

export interface SupplySettings {
  order_days: number[];                 // 每月固定下單日(1-31,1-6 個)
  lead_working_days: number;            // 下單 → 工廠出貨(交期,工作天)
  arrival_working_days: number;         // 工廠出貨 → 到貨(工作天)
  ship_working_days: number;            // 到貨 → 出給會員(工作天)
  grams_per_bottle: SupplyQty;          // 每瓶用原料(克)
  buffer_bottles: SupplyQty;            // 每批預設緩衝瓶數
  holidays: string[];                   // 不出貨日期 YYYY-MM-DD
  reminder_enabled: boolean;            // 下單前一天推播提醒
  default_carrier: string;              // CARRIER_OPTIONS 的 value 或 ""
}

export interface SupplyRawCheck {
  status: "enough" | "short" | "deducted";   // deducted = 已下單,原料早就扣過了
  balance_before: number | null;              // 輪到這批時的預估結餘(deducted 時為 null)
  short_grams: number;
}

/** 總覽列表與批次明細共用的欄位 */
export interface SupplyBatchSummary {
  batch_id: string;               // = 下單日 YYYY-MM-DD
  order_date: string;
  factory_ship_date: string;
  arrival_date: string;
  ship_date: string;              // 出給會員
  materialized: boolean;          // false = 名單還沒固定,依配送設定即時計算
  status: SupplyBatchStatus;
  status_label: string;
  line_count: number;             // 有瓶數的人數
  need: SupplyQty;                // 會員名單合計
  extra: SupplyQty;               // 其他需求
  buffer: SupplyQty;              // 緩衝
  stock_offset: SupplyQty;        // 扣除現有成品
  suggested: SupplyQty;           // need + extra + buffer - stock_offset(最低 0)
  ordered: SupplyQty | null;      // 確認下單前為 null
  ordered_at: string | null;
  ordered_by: string;
  grams_per_bottle: SupplyQty;
  raw_needed_grams: number;
  raw_grams_used: number;
  received: SupplyQty;
  damaged: SupplyQty;
  shipped_count: number;
  note: string;
  wallet_short_count: number;
  missing_address_count: number;
  inactive_member_count: number;
  ship_unknown_count: number;         // 出貨狀態不明(可能已扣存摺)的人數,要顧問查核
  raw_check: SupplyRawCheck | null;   // 已完成的批次為 null
}

export interface SupplyOverview {
  as_of: string;
  settings: SupplySettings;
  raw: { balance_grams: number };
  plans: { members: number; drink: number; fresh: number };
  batches: SupplyBatchSummary[];      // 還沒完成的批次,下單日由近到遠
  recent: SupplyBatchSummary[];       // 已完成的批次,新的在前
  next_order_date: string | null;     // 最近一個還沒下單的批次
}

export interface SupplyAddress {
  city: string;
  district: string;
  detail: string;
}

export interface SupplyPlanRow {
  member_uid: string;
  name: string;
  display_name: string;
  phone: string;
  address: SupplyAddress;
  address_text: string;
  address_ok: boolean;
  member_status: string;          // active / suspended / merged / missing ...
  drink: number;                  // 每批即飲瓶數
  fresh: number;                  // 每批鮮燉瓶數
  active: boolean;                // false = 暫停配送
  start_date: string;             // "" 或 YYYY-MM-DD(首次出貨不早於)
  note: string;
  wallet: SupplyQty;              // 目前存摺餘額
  updated_at: string | null;
  updated_by: string;
}

export interface SupplyPlanPayload {
  drink: number;
  fresh: number;
  active: boolean;
  start_date: string;
  note: string;
}

export type SupplyLineFlag =
  | "wallet_short"
  | "missing_address"
  | "member_suspended"
  | "member_merged"
  | "member_missing"
  | "ship_in_progress"   // 出貨處理中(已認領,還沒結果)
  | "ship_unknown";      // 出貨狀態不明:可能已扣存摺,要查核

// 出貨認領:按出貨時先鎖住這位會員與瓶數,同一位不會被出兩次;出完就解除
export interface SupplyClaim {
  claim_id: string;                 // 也是這次的存摺出貨單編號,查核時用來對帳
  drink: number;
  fresh: number;
  state: "shipping" | "resolving" | "unknown";
  at: string;
  operator: string;
  error?: string;
}

export interface SupplyShipped {
  shipment_id: string;
  ship_date: string;
  carrier: string;
  at: string;
  operator: string;
  drink: number;
  fresh: number;
}

export interface SupplyShipError {
  status: string;                 // insufficient_balance / member_not_found / unknown(狀態不明)/ fenced(已由查核接手)…
  category?: string;              // drink / fresh
  balance?: number;
  needed?: number;
  at?: string;
}

export interface SupplyLine {
  member_uid: string;
  name: string;
  display_name: string;
  phone: string;
  address: SupplyAddress;
  address_text: string;
  address_ok: boolean;
  member_status: string;
  drink: number;
  fresh: number;
  note: string;
  wallet: SupplyQty;                // 目前存摺餘額
  available: SupplyQty | null;      // 扣掉更早批次還沒出的瓶數後可用;已出貨為 null
  flags: SupplyLineFlag[];
  shipped: SupplyShipped | null;
  ship_error: SupplyShipError | null;
  claim: SupplyClaim | null;        // 出貨處理中 / 狀態不明:不能再出、不能改
}

export interface SupplyArrival {
  arrival_id: string;
  date: string;
  drink: number;
  fresh: number;
  damaged_drink: number;
  damaged_fresh: number;
  note: string;
  operator: string;
  at: string;
}

export interface SupplyBatchDetail extends SupplyBatchSummary {
  lines: SupplyLine[];
  arrivals: SupplyArrival[];
  shipped: Record<string, SupplyShipped>;
  ship_errors: Record<string, SupplyShipError | null>;
  settings: { default_carrier: string };
}

export interface SupplyBatchPatch {
  extra?: SupplyQty;
  buffer?: SupplyQty;
  stock_offset?: SupplyQty;
  note?: string;
}

export interface SupplyLinePayload {
  member_uid: string;
  drink: number;
  fresh: number;
  note: string;
}

export interface SupplyArrivalPayload {
  date: string;
  drink: number;
  fresh: number;
  damaged_drink: number;
  damaged_fresh: number;
  note: string;
}

export interface SupplyShipPayload {
  member_uids: string[];          // 一律明確帶,一次最多 20 位(後端上限 25)
  carrier: string;
  ship_date: string;
}

export interface SupplyShipFailed extends SupplyShipError {
  member_uid: string;
}

export interface SupplyRawLedgerRow {
  type: string;                   // purchase / count / batch_use / batch_adjust / batch_cancel
  type_label: string;
  grams: number;                  // 帶正負號
  balance_after: number;
  batch_id: string;
  note: string;
  entry_date: string;
  operator: string;
  created_at: string | null;
}

export interface SupplyRaw {
  balance_grams: number;
  ledger: SupplyRawLedgerRow[];   // 新的在前
}

export type SupplyRawPayload =
  | { type: "purchase"; grams: number; date: string; note: string }
  | { type: "count"; balance_grams: number; date: string; note: string };   // 盤點:填實際結餘,後端記差額

export interface SupplyReminderSummary {
  order_date: string;
  ship_date: string;
  arrival_date: string;
  status: SupplyBatchStatus;
  line_count: number;
  suggested: SupplyQty;
  ordered: SupplyQty | null;
  raw_needed_grams: number;
  raw_check: SupplyRawCheck | null;
  raw_balance_grams: number;
  wallet_short_count: number;
  missing_address_count: number;
  next_batch: {
    order_date: string;
    raw_check: SupplyRawCheck | null;
    raw_needed_grams: number;
  } | null;
}

/** 400 / 404 的錯誤本體(成功時 status = "ok") */
export interface SupplyError {
  status: string;
  category?: string;              // invalid_qty 等:哪個品類
  member_uid?: string;
  value?: string;
}

export type SupplyResult<T = unknown> = SupplyError & Partial<T>;

const SUPPLY_BIZ = {
  validateStatus: (s: number) => (s >= 200 && s < 300) || s === 400 || s === 404,
};

const SUPPLY = "/api/admin/supply";

export async function adminSupplyOverview() {
  const { data } = await api.get(`${SUPPLY}/overview`);
  return data as SupplyOverview;
}

// 規則設定

export async function adminSupplyGetSettings() {
  const { data } = await api.get(`${SUPPLY}/settings`);
  return data as SupplySettings;
}

export async function adminSupplyUpdateSettings(patch: Partial<SupplySettings>) {
  const { data } = await api.put(`${SUPPLY}/settings`, patch, SUPPLY_BIZ);
  return data as SupplyResult<{ settings: SupplySettings }>;
}

// 會員配送設定

export async function adminSupplyListPlans() {
  const { data } = await api.get(`${SUPPLY}/plans`);
  return data as { plans: SupplyPlanRow[] };
}

export async function adminSupplySavePlan(uid: string, plan: SupplyPlanPayload) {
  const { data } = await api.put(`${SUPPLY}/plans/${uid}`, plan, SUPPLY_BIZ);
  return data as SupplyResult;
}

export async function adminSupplyDeletePlan(uid: string) {
  const { data } = await api.delete(`${SUPPLY}/plans/${uid}`, SUPPLY_BIZ);
  return data as SupplyResult;
}

// 批次

export async function adminSupplyGetBatch(batchId: string) {
  const { data } = await api.get(`${SUPPLY}/batches/${batchId}`);
  return data as SupplyBatchDetail;
}

export async function adminSupplyUpdateBatch(batchId: string, patch: SupplyBatchPatch) {
  const { data } = await api.put(`${SUPPLY}/batches/${batchId}`, patch, SUPPLY_BIZ);
  return data as SupplyResult;
}

// 整份取代這一批名單;已出貨的會員後端一律保留
export async function adminSupplySetLines(batchId: string, lines: SupplyLinePayload[]) {
  const { data } = await api.put(`${SUPPLY}/batches/${batchId}/lines`, { lines }, SUPPLY_BIZ);
  return data as SupplyResult;
}

export async function adminSupplyRegenerate(batchId: string) {
  const { data } = await api.post(`${SUPPLY}/batches/${batchId}/regenerate`, {}, SUPPLY_BIZ);
  return data as SupplyResult;
}

// 確認下單 / 改下單瓶數;原料自動扣(改單只補扣或退差額)。raw_short = 扣完結餘為負
export async function adminSupplyConfirmOrder(batchId: string, qty: SupplyQty) {
  const { data } = await api.post(`${SUPPLY}/batches/${batchId}/order`, qty, SUPPLY_BIZ);
  return data as SupplyResult<{ raw_balance_grams: number; raw_short: boolean }>;
}

// 取消下單:已有到貨或出貨 → 400 has_arrivals_or_shipments
export async function adminSupplyCancelOrder(batchId: string) {
  const { data } = await api.delete(`${SUPPLY}/batches/${batchId}/order`, SUPPLY_BIZ);
  return data as SupplyResult<{ raw_balance_grams: number }>;
}

export async function adminSupplyAddArrival(batchId: string, arrival: SupplyArrivalPayload) {
  const { data } = await api.post(`${SUPPLY}/batches/${batchId}/arrivals`, arrival, SUPPLY_BIZ);
  return data as SupplyResult<{ arrival: SupplyArrival }>;
}

export async function adminSupplyDeleteArrival(batchId: string, arrivalId: string) {
  const { data } = await api.delete(`${SUPPLY}/batches/${batchId}/arrivals/${arrivalId}`, SUPPLY_BIZ);
  return data as SupplyResult;
}

// 批次出貨(每位會員一筆存摺出貨)。呼叫端自己把 member_uids 切成 20 位一組、一組一次呼叫;
// 不要照 remaining 迴圈重打 — 失敗的會員仍是未出貨,會永遠有 remaining
export async function adminSupplyShip(batchId: string, payload: SupplyShipPayload) {
  const { data } = await api.post(`${SUPPLY}/batches/${batchId}/ship`, payload, {
    ...SUPPLY_BIZ,
    timeout: 60000,   // 一組 20 位,每位要扣存摺 + 推 LINE
  });
  return data as SupplyResult<{
    shipped: (SupplyShipped & { member_uid: string })[];
    failed: SupplyShipFailed[];   // status "unknown" = 狀態不明,要查核
    skipped: { member_uid: string; reason: string }[];   // already_shipped / in_progress / not_in_list
    remaining: number;
  }>;
}

// 查核出貨狀態不明的會員,依出貨編號對帳:出貨單在 → 補記已出貨(resolution "shipped");
// 不在 → 把這次已扣的瓶數退回存摺(refunded)再解除鎖定("released")
export async function adminSupplyResolveClaim(batchId: string, uid: string) {
  const { data } = await api.post(`${SUPPLY}/batches/${batchId}/claims/${uid}/resolve`, {}, SUPPLY_BIZ);
  return data as SupplyResult<{ resolution: "shipped" | "released"; refunded: SupplyQty }>;
}

// 匯出 .xlsx(出貨名單 + 下單建議兩張工作表);回 Blob 由呼叫端觸發下載
export async function adminSupplyExport(batchId: string): Promise<Blob> {
  const { data } = await api.post(
    `${SUPPLY}/batches/${batchId}/export`,
    {},
    { responseType: "blob", timeout: 60000 },
  );
  return data as Blob;
}

// 原料帳

export async function adminSupplyGetRaw() {
  const { data } = await api.get(`${SUPPLY}/raw`);
  return data as SupplyRaw;
}

export async function adminSupplyRecordRaw(payload: SupplyRawPayload) {
  const { data } = await api.post(`${SUPPLY}/raw`, payload, SUPPLY_BIZ);
  return data as SupplyResult<{ balance_grams: number }>;
}

// 下單前提醒:預覽內容 / 立即推一則到管理群(沒綁群組 → 400 no_admin_group)

export async function adminSupplyReminderPreview(orderDate?: string) {
  const { data } = await api.get(`${SUPPLY}/reminder/preview`, {
    params: orderDate ? { order_date: orderDate } : {},
    ...SUPPLY_BIZ,
  });
  return data as SupplyResult<{ summary: SupplyReminderSummary }>;
}

export async function adminSupplyReminderTest(orderDate?: string) {
  const { data } = await api.post(
    `${SUPPLY}/reminder/test`,
    orderDate ? { order_date: orderDate } : {},
    SUPPLY_BIZ,
  );
  return data as SupplyResult<{ summary: SupplyReminderSummary }>;
}
